import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ServerError } from './api';
import { fa, group, toman } from './fa';
import { Shell } from './Shell';
import type { Session } from './Till';

/*
 * Opening the drawer — screen ۰۱ of the design.
 *
 * The opening float is the only thing on this screen that the shop cannot
 * reconstruct later. A drawer holding 500,000 at close is either exactly right
 * or 500,000 short depending on what was in it this morning, and no report can
 * tell the difference after the fact — so this is asked once, before the first
 * sale, rather than offered as something to fill in when convenient.
 */
export function OpenShiftScreen({
  session, onOpened,
}: {
  session: Session & { branches: { id: string; name: string }[] };
  onOpened: () => void;
}) {
  const [floatToman, setFloat] = useState(0);
  const [branchId, setBranchId] = useState(session.branchId);
  const [error, setError] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () => api.post<{ id: string }>('/till/shifts', { branchId, openingFloatToman: floatToman }),
    onSuccess: onOpened,
    onError: (e: ServerError) => setError(e.detail ?? e.message),
  });

  function press(k: string) {
    setFloat((v) => {
      let s = String(v);
      if (k === 'del') s = s.length > 1 ? s.slice(0, -1) : '0';
      else if (k === '000') s = s === '0' ? '0' : s + '000';
      else s = s === '0' ? k : s + k;
      return Math.min(999_999_999, parseInt(s, 10) || 0);
    });
  }

  return (
    <Shell
      branchName={session.branches.find((b) => b.id === branchId)?.name ?? session.branchName}
      cashierName={session.name}
      cashierInitials={session.name.slice(0, 2)}
      shiftSince={null}
      net="online"
      queued={0}
    >
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
              ۰۱ · شروع روز
            </div>
            <div style={{ fontWeight: 800, fontSize: 24 }}>موجودی اولیه کشو را وارد کنید</div>
          </div>

          <div
            className="pos-num"
            style={{
              height: 84, border: '1px solid var(--luma-line-strong)', borderRadius: 12,
              background: 'var(--luma-sunken)', display: 'flex', alignItems: 'center',
              padding: '0 18px', fontWeight: 800, fontSize: 36,
              boxShadow: 'inset 0 0 0 3px rgba(249,124,21,.08)',
            }}
          >
            <span style={{ flex: 1 }}>{group(floatToman)}</span>
            <span style={{ fontSize: 15, color: 'var(--luma-muted)', fontWeight: 600 }}>تومان</span>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[0, 200_000, 500_000, 1_000_000].map((v) => (
              <button
                key={v}
                onClick={() => setFloat(v)}
                style={{
                  height: 48, padding: '0 16px', borderRadius: 10,
                  border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                  fontWeight: 700, fontSize: 14,
                }}
              >
                {v === 0 ? 'خالی' : group(v)}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, width: 320 }}>
            {['7', '8', '9', '4', '5', '6', '1', '2', '3', '000', '0', 'del'].map((k) => (
              <button
                key={k}
                onClick={() => press(k)}
                style={{
                  height: 60, borderRadius: 12, border: '1px solid var(--luma-line)',
                  background: 'var(--luma-card)', fontFamily: 'var(--luma-font-money)',
                  fontWeight: 700, fontSize: 21,
                }}
              >
                {k === 'del' ? '⌫' : fa(k)}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            width: 428, flex: 'none', background: 'var(--luma-card)',
            borderInlineStart: '1px solid var(--luma-line)', padding: 18,
            display: 'flex', flexDirection: 'column', gap: 14,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
            باز کردن شیفت
          </div>

          {session.branches.length > 1 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-ink-2)', marginBottom: 6 }}>شعبه</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {session.branches.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setBranchId(b.id)}
                    style={{
                      height: 52, borderRadius: 10, textAlign: 'right', padding: '0 14px',
                      fontWeight: 700, fontSize: 14,
                      border: `1px solid ${branchId === b.id ? 'var(--luma-accent)' : 'var(--luma-line-strong)'}`,
                      background: branchId === b.id ? 'var(--luma-accent-tint)' : 'var(--luma-card)',
                      color: branchId === b.id ? 'var(--luma-accent-tint-text)' : undefined,
                    }}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div
            style={{
              background: 'var(--luma-sunken)', border: '1px solid var(--pos-rule-strong)',
              borderRadius: 12, padding: '12px 14px', fontSize: 12.5,
              color: 'var(--pos-ink-3)', lineHeight: 1.65,
            }}
          >
            {/*
              Named as not-connected rather than shown as four green ticks.
              A hardware panel that always says "متصل" is one the cashier stops
              reading, which makes it useless on the morning something is
              actually unplugged.
            */}
            کارت‌خوان، چاپگر و ترازو از طریق دستگاه محلی وصل می‌شوند و هنوز روی این صندوق فعال نیستند —
            پرداخت کارتی با ثبت دستی شماره پیگیری انجام می‌شود.
          </div>

          {error && (
            <div
              style={{
                background: 'var(--luma-tint-danger-bg)', border: '1px solid var(--luma-tint-danger-border)',
                borderRadius: 10, padding: '10px 14px', fontSize: 13.5,
                color: 'var(--luma-tint-danger-text)', fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ flex: 1 }} />

          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              borderTop: '1px solid var(--pos-rule)', paddingTop: 12,
            }}
          >
            <span style={{ fontSize: 13.5, color: 'var(--luma-ink-2)', fontWeight: 600 }}>موجودی اولیه</span>
            <span className="pos-num" style={{ fontWeight: 800, fontSize: 24 }}>{toman(floatToman * 10)}</span>
          </div>

          <button
            onClick={() => open.mutate()}
            disabled={open.isPending}
            style={{
              height: 60, borderRadius: 12, background: 'var(--luma-accent)', color: '#FFF',
              fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', padding: '0 20px',
              boxShadow: '0 3px 10px rgba(249,124,21,.28)',
            }}
          >
            <span>{open.isPending ? 'در حال باز کردن…' : 'باز کردن شیفت و شروع فروش'}</span>
            <span style={{ fontSize: 18 }}>←</span>
          </button>
        </div>
      </div>
    </Shell>
  );
}
